import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { Form, Input, DatePicker, Select, Button } from "antd";
import { FormInstance } from "antd/lib/form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SelectProps } from "antd/lib/select";
import { dayjs } from "Config/dayjs";

interface MetadataEditInputProps {
  value?: { key: string; value: string };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange?: (val: any) => void;
  format: string;
  rangeList?: API_Metadata["rangeList"];
}

const MetadataEditInput = ({ value, onChange, format, rangeList = [] }: MetadataEditInputProps) => {
  // tslint:disable-next-line: max-line-length
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ key: value.key, value: e.target.value });
  const handleChange = (val) => onChange({ key: value.key, value: val });
  if (format === "W") {
    const options: SelectProps<string>["options"] = rangeList.map((item) => ({
      value: item.value,
      label: item.value,
    }));
    return (
      <Select
        mode="multiple"
        placeholder={value.key}
        value={value.value}
        onChange={handleChange}
        style={{ width: "60%" }}
        options={options}
      />
    );
  } else if (format === "DATE") {
    return (
      <DatePicker
        placeholder={value.key}
        value={dayjs(value.value)}
        format="MM.DD.YYYY"
        onChange={handleChange}
        style={{ width: "60%" }}
      />
    );
  } else {
    return <Input placeholder={value.key} value={value.value} onChange={handleInputChange} style={{ width: "60%" }} />;
  }
};

export interface EditViewFiltersProps extends WithTranslation, ModalComponentProps {
  initialData: API_ViewFilter;
  metadataDefinitions: API_ViewInformation["metadata"];
  onSubmit: (formData: API_ViewFilter) => Promise<void>;
}

export const EditViewFilters = withTranslation([I18N_NS.GENERAL, I18N_NS.PROJECT])(
  class extends React.Component<EditViewFiltersProps, { loading: boolean }> {
    formRef: React.RefObject<FormInstance> = React.createRef();
    constructor(props: EditViewFiltersProps) {
      super(props);
      this.state = { loading: false };
    }

    handleFinish = async ({ metadata, created, ...restValues }) => {
      try {
        this.setState({ loading: true });
        if (created?.length) {
          created = [created[0].startOf("day"), created[1].endOf("day")];
        }
        const initialMetadata = this.props.initialData.metadata || {};
        const formData: API_ViewFilter = {
          ...this.props.initialData,
          ...restValues,
          created,
          metadata: (metadata || []).reduce(
            (obj, curr) => {
              obj[curr.key] = Array.isArray(curr.value) ? curr.value : [curr.value];
              return obj;
            },
            { ...initialMetadata }
          ),
        };
        await this.props.onSubmit(formData);
        this.props.hideModal();
      } catch (error) {
        throw error;
      } finally {
        this.setState({ loading: false });
      }
    };

    render() {
      const { t, initialData, metadataDefinitions } = this.props;
      const formInitialData = {
        ...initialData,
        created: initialData?.created?.map((date) => dayjs(date)) || null,
        metadata: Object.entries(initialData.metadata || {}).map((item) => ({
          key: item[0],
          value: item[1],
        })),
      };
      return (
        <Form ref={this.formRef} onFinish={this.handleFinish} layout="vertical" initialValues={formInitialData}>
          <Form.Item label={t("Name")} name="name" rules={[{ required: true, message: t("This field is required") }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t("Last change")} name="created">
            <DatePicker.RangePicker format="MM.DD.YYYY" placeholder={[t("From"), t("To")]} />
          </Form.Item>

          <Form.List name="metadata">
            {(fields, { remove }) => {
              // when component is initialized - getFieldValue returns undefined. Therefore we use initialData.
              const dataFromFormField = this.formRef.current?.getFieldValue("metadata");
              const metadataFieldVal = dataFromFormField ? dataFromFormField : formInitialData.metadata;
              return (
                <>
                  {fields.map((field, index) => {
                    // find metadata definition for each input field
                    const metadataKey = String(metadataFieldVal[index].key).replace(/^metadata_/, "");
                    const currentMetadataInfo = metadataDefinitions.find((item) => item.key === metadataKey);
                    if (!currentMetadataInfo) {
                      return null;
                    }
                    return (
                      <Form.Item label={currentMetadataInfo.key} required={false} key={field.key}>
                        <Form.Item
                          {...field}
                          validateTrigger={["onChange", "onBlur"]}
                          rules={[
                            {
                              required: true,
                              message:
                                index === 0
                                  ? t("Please select metadata")
                                  : t("Please select metadata or delete this field."),
                            },
                          ]}
                          noStyle
                        >
                          <MetadataEditInput
                            format={currentMetadataInfo.format}
                            rangeList={currentMetadataInfo.rangeList}
                          />
                        </Form.Item>
                        {fields.length > 1 ? (
                          <FontAwesomeIcon
                            icon={["far", "minus-circle"]}
                            fixedWidth={true}
                            className="dynamic-delete-button"
                            onClick={() => remove(field.name)}
                          />
                        ) : null}
                      </Form.Item>
                    );
                  })}
                  {/* <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      style={{ width: '100%' }}
                      faIcon={'plus'}
                    >
                      {t('Add field')}
                    </Button>
                  </Form.Item> */}
                </>
              );
            }}
          </Form.List>
          <div className="text-right">
            <Button type="primary" htmlType="submit" loading={this.state.loading}>
              {t("Save")}
            </Button>
          </div>
        </Form>
      );
    }
  }
);
